import { defineComponent, ref, computed, inject, watch, onBeforeMount } from 'vue';
import { format } from 'date-fns';
import { useLocalizationSupport } from '../../composables/localization-support/index.js';
import { useStoreModule } from '../../composables/store-module/index.js';
import { factsheetHeaderFactory } from '../factsheet-header/setup/index.js';
import HoistComponent from '../hoist-component/HoistComponent.vue.js';
import UiRenderNothing from '../ui-render-nothing/UiRenderNothing.vue.js';
import { getValidationErrors } from '../../helpers/validation.js';
import { generateElementId } from '../../helpers/generators.js';

var _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "PortfolioTransactionAdd",
  props: {
    locale: { type: null, required: true },
    id: { type: null, required: true },
    securityId: { type: null, required: true },
    type: { type: null, required: false }
  },
  emits: ["store-error", "data-load", "submit", "cancel", "error", "exchange-change"],
  setup(__props, { emit: emits }) {
    const props = __props;
    const DATE_INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";
    const action = ref("FETCH");
    const transactionType = ref("buy");
    const buyPrice = ref(null);
    const buyDate = ref(format(new Date(), DATE_INPUT_FORMAT));
    const quantity = ref(1);
    const portfolioPositionCurrency = ref(null);
    const buyValueCurrency = ref(null);
    const portfolioCurrency = ref(null);
    const isPositionCurrencyPercent = ref(false);
    const validationTransactionTypeErrorsBag = ref([]);
    const validationBuyDateErrorsBag = ref([]);
    const validationBuyPriceErrorsBag = ref([]);
    const validationExchangeRateErrorsBag = ref([]);
    const validationQuantityErrorsBag = ref([]);
    const exchangeRate = ref(1);
    const transactionBuyRadioId = generateElementId("fds-transaction-buy-");
    const transactionSellRadioId = generateElementId("fds-transaction-sell-");
    const quantityInputId = generateElementId("fds-quantity-");
    const buyPriceInputId = generateElementId("fds-buy-price-");
    const buyDateInputId = generateElementId("fds-buy-date-");
    const exchangeRateInputId = generateElementId("fds-exchange-rate-");
    const currentQuantityInputId = generateElementId("fds-current-quantity-");
    const buyValueInputId = generateElementId("fds-buy-value-rate-");
    const buyValueInBaseCurrencyInputId = generateElementId("fds-buy-value-in-base-currency-");
    const componentInputs = computed(() => ({
      id: props.id,
      securityId: props.securityId,
      action: action.value,
      transactionType: transactionType.value,
      buyDate: new Date(buyDate.value),
      buyPrice: Number.parseFloat(String(buyPrice.value)),
      exchangeRate: Number.parseFloat(String(exchangeRate.value)),
      quantity: Number.parseInt(String(quantity.value), 10),
      language: props.locale
    }));
    const maxDate = computed(() => {
      return format(new Date(), DATE_INPUT_FORMAT);
    });
    const buyValueInBaseCurrency = computed(() => {
      const value = buyPrice.value ? buyPrice.value * exchangeRate.value * Number.parseInt(quantity.value, 10) : null;
      if (isPositionCurrencyPercent.value && value) {
        return value / 100;
      }
      return value;
    });
    const buyValue = computed(() => {
      const value = buyPrice.value ? buyPrice.value * Number.parseInt(quantity.value, 10) : null;
      if (isPositionCurrencyPercent.value && value) {
        return value / 100;
      }
      return value;
    });
    const sameCurrency = computed(() => {
      return portfolioPositionCurrency.value === portfolioCurrency.value || buyValueCurrency.value === portfolioCurrency.value;
    });
    const canSell = computed(() => {
      var _a, _b;
      if (quantity.value === "") {
        return false;
      }
      const numberOfShares = (_b = (_a = storeData.value) == null ? void 0 : _a.portfolio.position.numberOfShares) != null ? _b : 0;
      return numberOfShares > 0 && quantity.value <= numberOfShares;
    });
    const transactionValueInPortfolioCurrencyLabelKey = computed(() => {
      return transactionType.value === "buy" ? "buyValueInPortfolioCurrency" : "sellValueInPortfolioCurrency";
    });
    const transactionValueInNotationCurrencyLabelKey = computed(() => {
      return transactionType.value === "buy" ? "buyValueInNotationCurrency" : "sellValueInNotationCurrency";
    });
    const transactionPriceLabelKey = computed(() => {
      return transactionType.value === "buy" ? "buyPrice" : "sellPrice";
    });
    const transactionDateLabelKey = computed(() => {
      return transactionType.value === "buy" ? "buyDate" : "sellDate";
    });
    const translator = inject("translator");
    const numberFormatter = inject("numberFormatter");
    const dateFormatter = inject("dateFormatter");
    const injectedStoreModule = inject("storeModule");
    const localizationSupport = useLocalizationSupport(props, {
      translator,
      numberFormatter,
      dateFormatter
    });
    const { translate, textFormat, numberFormat } = localizationSupport;
    const storeModule = useStoreModule(
      {
        storeModule: injectedStoreModule
      },
      componentInputs,
      emits,
      {
        requestDataOnComponentInputsChange: false
      }
    );
    const { storeData, storeMeta, isLoadingStoreData, storeRequestData, storeErrorsBag, storeCleanUp } = storeModule;
    const validateTransactionType = () => {
      var _a;
      validationTransactionTypeErrorsBag.value = getValidationErrors(transactionType.value, (_a = storeMeta.value) == null ? void 0 : _a.validationRules.transactionType);
    };
    const hasValidTransactionType = () => {
      return validationTransactionTypeErrorsBag.value.length === 0;
    };
    const validateBuyDate = () => {
      var _a;
      validationBuyDateErrorsBag.value = getValidationErrors(buyDate.value, (_a = storeMeta.value) == null ? void 0 : _a.validationRules.buyDate);
    };
    const hasValidBuyDate = () => {
      return validationBuyDateErrorsBag.value.length === 0;
    };
    const validateBuyPrice = () => {
      var _a, _b;
      validationBuyPriceErrorsBag.value = getValidationErrors((_a = buyPrice.value) != null ? _a : "", (_b = storeMeta.value) == null ? void 0 : _b.validationRules.buyPrice);
    };
    const FactsheetHeader = factsheetHeaderFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().factsheetHeader : void 0
    });
    const hasValidBuyPrice = () => {
      return validationBuyPriceErrorsBag.value.length === 0;
    };
    const validateExchangeRate = () => {
      var _a;
      validationExchangeRateErrorsBag.value = getValidationErrors(exchangeRate.value, (_a = storeMeta.value) == null ? void 0 : _a.validationRules.exchangeRate);
    };
    const hasValidExchangeRate = () => {
      return validationExchangeRateErrorsBag.value.length === 0;
    };
    const validateQuantity = () => {
      var _a;
      validationQuantityErrorsBag.value = getValidationErrors(quantity.value, (_a = storeMeta.value) == null ? void 0 : _a.validationRules.quantity);
      if (transactionType.value === "sell" && !canSell.value) {
        validationQuantityErrorsBag.value.push({
          label: "limitExceeded"
        });
      }
    };
    const hasValidQuantity = () => {
      return validationQuantityErrorsBag.value.length === 0;
    };
    const validateForm = () => {
      validateBuyDate();
      validateBuyPrice();
      validateExchangeRate();
      validateQuantity();
      validateTransactionType();
    };
    const hasValidForm = () => {
      return hasValidBuyDate() && hasValidBuyPrice() && hasValidExchangeRate() && hasValidQuantity() && hasValidTransactionType();
    };
    const onSubmit = async () => {
      validateForm();
      if (hasValidForm()) {
        action.value = "ADD";
        await storeRequestData();
        action.value = "FETCH";
        await storeRequestData();
        init();
        emits("submit");
      }
    };
    const onTransactionAddError = () => {
      if (storeErrorsBag.value.length > 0) {
        emits("error", storeErrorsBag.value);
      }
    };
    watch([() => storeErrorsBag], () => {
      onTransactionAddError();
    });
    const onCancel = () => {
      emits("cancel");
    };
    const onExchangeChange = (payload) => {
      emits("exchange-change", payload);
    };
    const init = () => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i;
      if (props.type !== void 0) {
        transactionType.value = props.type;
      }
      buyValueCurrency.value = (_a = storeData.value) == null ? void 0 : _a.portfolio.position.buyValue.currency;
      portfolioCurrency.value = (_b = storeData.value) == null ? void 0 : _b.portfolio.currency;
      portfolioPositionCurrency.value = (_c = storeData.value) == null ? void 0 : _c.portfolio.position.currency;
      isPositionCurrencyPercent.value = (_e = (_d = storeData.value) == null ? void 0 : _d.portfolio.position.currencyIsPercent) != null ? _e : false;
      exchangeRate.value = !sameCurrency.value ? (_g = (_f = storeData.value) == null ? void 0 : _f.portfolio.position.exchangeRate) != null ? _g : 1 : 1;
      buyPrice.value = (_i = (_h = storeData.value) == null ? void 0 : _h.portfolio.position.lastPrice) != null ? _i : null;
    };
    onBeforeMount(() => {
      void storeRequestData().then(init);
    });
    watch([() => [props.id, props.securityId, props.locale]], () => {
      void storeCleanUp(componentInputs.value).then(storeRequestData).then(init);
    });
    return { __sfc: true, DATE_INPUT_FORMAT, action, transactionType, buyPrice, buyDate, quantity, portfolioPositionCurrency, buyValueCurrency, portfolioCurrency, isPositionCurrencyPercent, validationTransactionTypeErrorsBag, validationBuyDateErrorsBag, validationBuyPriceErrorsBag, validationExchangeRateErrorsBag, validationQuantityErrorsBag, exchangeRate, transactionBuyRadioId, transactionSellRadioId, quantityInputId, buyPriceInputId, buyDateInputId, exchangeRateInputId, currentQuantityInputId, buyValueInputId, buyValueInBaseCurrencyInputId, props, emits, componentInputs, maxDate, buyValueInBaseCurrency, buyValue, sameCurrency, canSell, transactionValueInPortfolioCurrencyLabelKey, transactionValueInNotationCurrencyLabelKey, transactionPriceLabelKey, transactionDateLabelKey, translator, numberFormatter, dateFormatter, injectedStoreModule, localizationSupport, translate, textFormat, numberFormat, storeModule, storeData, storeMeta, isLoadingStoreData, storeRequestData, storeErrorsBag, storeCleanUp, validateTransactionType, hasValidTransactionType, validateBuyDate, hasValidBuyDate, validateBuyPrice, FactsheetHeader, hasValidBuyPrice, validateExchangeRate, hasValidExchangeRate, validateQuantity, hasValidQuantity, validateForm, hasValidForm, onSubmit, onTransactionAddError, onCancel, onExchangeChange, init, HoistComponent, UiRenderNothing };
  }
});

export { _sfc_main as default };
